import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Tab, Combobox } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import InProgressModal from './InProgressModal';
import CloseTaskModal from './CloseTaskModal';
import RichTextEditor from './RichTextEditor';
import RichTextDisplay from './RichTextDisplay';
import DateTimePickerInput from './DateTimePickerInput';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { dateToUTCNoon } from '../utils/dateHelpers';
import {
  problemTaskTypeLabels,
  problemTaskAssignedToLabels,
  problemTaskStateLabels,
  getDisplayLabel
} from '../utils/labelMappings';
import { sanitizeNoteInput, hasActualTextContent } from '../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface ProblemTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (shouldClose?: boolean) => void;
  problemId: string;
  problemNumber: string;
  engagementId: string | null;
  taskType: string;
  existingTask?: any | null;
  customerId?: string | null;
}

interface Resource {
  id: string;
  name: string;
  resourceType: string;
  status: string;
}

interface ProblemTaskNote {
  id: string;
  problemTaskId: string;
  authorId: string;
  note: string;
  isPublic: boolean;
  timestamp: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ProblemTaskActivity {
  id: string;
  problemTaskId: string;
  activityType: string;
  previousState?: string;
  newState?: string;
  metadata?: any;
  performedById: string;
  performedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const ProblemTaskModal: React.FC<ProblemTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  problemId,
  problemNumber,
  engagementId,
  taskType,
  existingTask,
  customerId
}) => {
  const { getToday } = useDateFormatter();
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [resourceId, setResourceId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [serviceImprovements, setServiceImprovements] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [taskState, setTaskState] = useState<string>('new');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Assignment state
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [assignedToId, setAssignedToId] = useState<string>('');

  // Combobox search queries
  const [ownerQuery, setOwnerQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');

  // Notes state
  const [newNote, setNewNote] = useState<string>('');
  const [noteType, setNoteType] = useState<'internal' | 'customer'>('internal');
  const [addingNote, setAddingNote] = useState(false);

  // In Progress Modal state
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [showCloseTaskModal, setShowCloseTaskModal] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState<string | null>(null);

  const isEditMode = Boolean(existingTask);
  const queryClient = useQueryClient();

  // Fetch full task data when editing (to get assignment info)
  const { data: fullTaskData } = useQuery({
    queryKey: ['problem-task', existingTask?.id],
    queryFn: async () => {
      if (!existingTask?.id) return null;
      const response = await api.get(`/problem-tasks/${existingTask.id}`);
      return response.data;
    },
    enabled: Boolean(existingTask?.id) && isOpen
  });

  // Use fullTaskData if available, otherwise use existingTask
  const taskData = fullTaskData || existingTask;

  // Debug logging
  React.useEffect(() => {
    if (taskData && isEditMode) {
      console.log('Task Data:', taskData);
      console.log('AssignmentGroup:', taskData?.AssignmentGroup);
      console.log('AssignedToUser:', taskData?.AssignedToUser);
    }
  }, [taskData, isEditMode]);

  // Fetch resources for the engagement
  const { data: resources, isLoading: loadingResources } = useQuery<Resource[]>({
    queryKey: ['engagement-resources', engagementId],
    queryFn: async () => {
      if (!engagementId) return [];
      const response = await api.get(`/engagements/${engagementId}/resources`);
      return response.data.resources || [];
    },
    enabled: Boolean(engagementId) && isOpen
  });

  // Fetch assignment groups
  const { data: assignmentGroups } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups', {
        params: { type: 'assignment', isActive: true, limit: 1000 }
      });
      return response.data.groups || [];
    },
    enabled: isOpen && isEditMode
  });

  // Fetch group members when assignment group is selected
  const { data: groupMembers } = useQuery({
    queryKey: ['group-members', assignmentGroupId, customerId],
    queryFn: async () => {
      if (!assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(assignmentGroupId, customerId || null);
    },
    enabled: Boolean(assignmentGroupId) && isOpen && isEditMode
  });

  // Fetch notes for the task
  const { data: notes, isLoading: loadingNotes } = useQuery<ProblemTaskNote[]>({
    queryKey: ['problem-task-notes', existingTask?.id],
    queryFn: async () => {
      if (!existingTask?.id) return [];
      const response = await api.get(`/problem-tasks/${existingTask.id}/notes`);
      return response.data;
    },
    enabled: Boolean(existingTask?.id) && isOpen
  });

  // Fetch activities for the task
  const { data: activities, isLoading: loadingActivities } = useQuery<ProblemTaskActivity[]>({
    queryKey: ['problem-task-activities', existingTask?.id],
    queryFn: async () => {
      if (!existingTask?.id) return [];
      const response = await api.get(`/problem-tasks/${existingTask.id}/activities`);
      console.log('Received activities from API:', response.data.length);
      console.log('First activity:', JSON.stringify(response.data[0]));
      return response.data;
    },
    enabled: Boolean(existingTask?.id) && isOpen
  });

  // Owner options for combobox
  const ownerOptions = Object.entries(problemTaskAssignedToLabels).map(([key, label]) => ({
    id: key,
    name: label as string
  }));

  // Filtered data for comboboxes
  const filteredOwnerOptions = ownerQuery === ''
    ? ownerOptions
    : ownerOptions.filter((option) =>
        option.name.toLowerCase().includes(ownerQuery.toLowerCase())
      );

  const filteredResources = resourceQuery === ''
    ? (resources || [])
    : (resources || []).filter((resource) =>
        resource.name.toLowerCase().includes(resourceQuery.toLowerCase()) ||
        resource.resourceType.toLowerCase().includes(resourceQuery.toLowerCase())
      );

  const filteredGroups = groupQuery === ''
    ? (assignmentGroups || [])
    : (assignmentGroups || []).filter((group: any) =>
        group.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const filteredMembers = userQuery === ''
    ? (groupMembers || [])
    : (groupMembers || []).filter((member: any) => {
        const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
        return fullName.includes(userQuery.toLowerCase()) ||
               member.email?.toLowerCase().includes(userQuery.toLowerCase());
      });

  // Get selected items for display
  const selectedOwner = ownerOptions.find(o => o.id === assignedTo);
  const selectedResource = (resources || []).find(r => r.id === resourceId);
  const selectedGroup = (assignmentGroups || []).find((g: any) => g.id === assignmentGroupId);
  const selectedUser = (groupMembers || []).find((m: any) => m.id === assignedToId);

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (noteData: { note: string; isPublic: boolean }) => {
      const response = await api.post(`/problem-tasks/${existingTask.id}/notes`, noteData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem-task-notes', existingTask?.id] });
      queryClient.invalidateQueries({ queryKey: ['problem-task-activities', existingTask?.id] });
      setNewNote('');
      toast.success('Note added successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add note');
    }
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      await api.delete(`/problem-tasks/${existingTask.id}/notes/${noteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem-task-notes', existingTask?.id] });
      toast.success('Note deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete note');
    }
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (existingTask) {
        // Populate form with existing task data
        setAssignedTo(existingTask.assignedTo || '');
        setResourceId(existingTask.resourceId || '');
        setDescription(existingTask.description || '');
        setServiceImprovements(existingTask.serviceImprovements || '');
        setDueDate(existingTask.dueDate ? existingTask.dueDate.split('T')[0] : '');
        setTaskState(existingTask.state || 'new');
        setAssignmentGroupId(existingTask.assignmentGroupId || '');
        setAssignedToId(existingTask.assignedToId || '');
      } else {
        // Reset form for new task
        setAssignedTo('');
        setResourceId('');
        setDescription('');
        setServiceImprovements('');
        setDueDate('');
        setTaskState('new');
        setAssignmentGroupId('');
        setAssignedToId('');
      }
      setError(null);
    }
  }, [isOpen, existingTask]);

  // Helper function to convert date string to UTC (uses dateToUTCNoon utility)
  const formatDateForAPI = (dateString: string): string | null => {
    if (!dateString) return null;
    return dateToUTCNoon(dateString);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!assignedTo) {
      setError('Owner is required');
      return;
    }
    if (!description.trim()) {
      setError('Task Description is required');
      return;
    }
    // Assignment Group is required in edit mode
    if (isEditMode && !assignmentGroupId) {
      setError('Assignment Group is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        // Update existing task
        await api.put(`/problem-tasks/${existingTask.id}`, {
          assignedTo,
          resourceId: resourceId || null,
          description,
          serviceImprovements,
          dueDate: formatDateForAPI(dueDate),
          assignmentGroupId: assignmentGroupId || null,
          assignedToId: assignedToId || null
        });
        toast.success('Task updated successfully');
        // Invalidate queries to refresh the data
        queryClient.invalidateQueries({ queryKey: ['problem-task-activities', existingTask.id] });
        queryClient.invalidateQueries({ queryKey: ['problem-task', existingTask.id] });
        // Close the modal after saving
        onSuccess(true);
      } else {
        // Create new task
        await api.post(`/cases/${problemId}/problem-tasks`, {
          type: taskType,
          assignedTo,
          resourceId: resourceId || null,
          description,
          serviceImprovements,
          dueDate: formatDateForAPI(dueDate)
        });
        toast.success('Task created successfully');
        // For new task, refresh and close
        onSuccess(true);
      }
    } catch (error: any) {
      console.error('Error saving problem task:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save problem task';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStateChange = async (newState: string) => {
    if (!existingTask) return;

    // If transitioning to in_progress, check if Assignment Group is set
    if (newState === 'in_progress' && taskState !== 'in_progress') {
      // Use taskData which has the full task info including assignmentGroupId
      const currentAssignmentGroupId = taskData?.assignmentGroupId || assignmentGroupId;
      if (!currentAssignmentGroupId) {
        setError('Please select an Assignment Group before moving to In Progress.');
        toast.error('Please select an Assignment Group before moving to In Progress.');
        return;
      }
      // Proceed with state change directly (no modal)
      await performStateChange(newState, null, null, null);
      return;
    }

    // If transitioning to closed, check if Assigned To is set first
    if (newState === 'closed' && taskState !== 'closed') {
      // Check if Assigned To is set on the task
      const currentAssignedToId = taskData?.assignedToId || assignedToId;
      if (!currentAssignedToId) {
        setError('Please select an Assigned To user before closing this task.');
        toast.error('Please select an Assigned To user before closing this task.');
        return;
      }
      // Show the close task modal for closure details
      setPendingStateChange(newState);
      setShowCloseTaskModal(true);
      return;
    }

    // Otherwise, proceed with normal state change
    await performStateChange(newState, null, null, null);
  };

  const performStateChange = async (
    newState: string,
    assignmentGroupId: string | null,
    assignedToId: string | null,
    closureData?: {
      closedCode: string;
      causeCode: string;
      causeNotes: string;
      proposedFixNotes: string;
      workaround: string;
    } | null
  ) => {
    if (!existingTask) return;

    setLoading(true);
    setError(null);

    try {
      const payload: any = { state: newState };
      if (assignmentGroupId !== null) {
        payload.assignmentGroupId = assignmentGroupId;
      }
      if (assignedToId !== null) {
        payload.assignedToId = assignedToId;
      }
      if (closureData) {
        payload.closedCode = closureData.closedCode;
        payload.causeCode = closureData.causeCode;
        payload.causeNotes = closureData.causeNotes;
        payload.proposedFixNotes = closureData.proposedFixNotes;
        payload.workaround = closureData.workaround;
      }

      await api.patch(`/problem-tasks/${existingTask.id}/state`, payload);

      setTaskState(newState);
      toast.success(`Task state changed to ${getDisplayLabel(newState, problemTaskStateLabels)}`);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['problem-task-activities', existingTask.id] });
      queryClient.invalidateQueries({ queryKey: ['problem-task', existingTask.id] });
      // Refresh the task data to get updated assignment info
      onSuccess(false);
    } catch (error: any) {
      console.error('Error updating task state:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update task state';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const canTransitionTo = (fromState: string, toState: string) => {
    // State transition rules:
    // - From new: can go to in_progress or closed
    // - From in_progress: can only go to closed (cannot go back to new)
    // - From closed: can reopen to new or in_progress
    const validTransitions: Record<string, string[]> = {
      new: ['in_progress', 'closed'],
      in_progress: ['closed'],
      closed: ['new', 'in_progress']
    };
    return validTransitions[fromState]?.includes(toState) || false;
  };

  const handleAddNote = async () => {
    const sanitizedNote = sanitizeNoteInput(newNote);
    if (!hasActualTextContent(sanitizedNote)) {
      toast.error('Note cannot be empty');
      return;
    }

    setAddingNote(true);
    try {
      await addNoteMutation.mutateAsync({
        note: sanitizedNote,
        isPublic: noteType === 'customer'
      });
      setNoteType('internal');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      await deleteNoteMutation.mutateAsync(noteId);
    }
  };

  const getActivityDescription = (activity: ProblemTaskActivity) => {
    switch (activity.activityType) {
      case 'CREATED':
        return 'Task Created';
      case 'UPDATED':
        return 'Fields Updated';
      case 'STATE_CHANGED':
        return 'State Changed';
      case 'NOTE_ADDED_INTERNAL':
        return 'Internal Note Added';
      case 'NOTE_ADDED_CUSTOMER':
        return 'Customer Note Added';
      default:
        return activity.activityType;
    }
  };

  const getActivityDetails = (activity: ProblemTaskActivity) => {
    const metadata = activity.metadata as any;
    const details: string[] = [];

    if (activity.activityType === 'STATE_CHANGED') {
      if (activity.previousState && activity.newState) {
        details.push(`${getDisplayLabel(activity.previousState, problemTaskStateLabels)} → ${getDisplayLabel(activity.newState, problemTaskStateLabels)}`);
      }
    } else if (activity.activityType === 'UPDATED' && metadata) {
      // Show field changes with old → new format
      // Handle both new format (object with old/new) and old format (direct value)
      if (metadata.assignedTo) {
        if (typeof metadata.assignedTo === 'object' && metadata.assignedTo !== null && 'old' in metadata.assignedTo && 'new' in metadata.assignedTo) {
          const oldVal = metadata.assignedTo.old ? getDisplayLabel(metadata.assignedTo.old, problemTaskAssignedToLabels) : 'None';
          const newVal = getDisplayLabel(metadata.assignedTo.new, problemTaskAssignedToLabels);
          details.push(`Owner: ${oldVal} → ${newVal}`);
        }
        // Skip old format to avoid showing incomplete data
      }
      if (metadata.description) {
        if (typeof metadata.description === 'object' && metadata.description !== null && 'old' in metadata.description && 'new' in metadata.description) {
          details.push(`Description: Updated`);
        }
        // Skip old format
      }
      if (metadata.serviceImprovements) {
        if (typeof metadata.serviceImprovements === 'object' && metadata.serviceImprovements !== null && 'old' in metadata.serviceImprovements && 'new' in metadata.serviceImprovements) {
          details.push(`Service Improvements: Updated`);
        }
        // Skip old format
      }
      if (metadata.dueDate) {
        if (typeof metadata.dueDate === 'object' && metadata.dueDate !== null && 'old' in metadata.dueDate && 'new' in metadata.dueDate) {
          const oldVal = metadata.dueDate.old ? new Date(metadata.dueDate.old).toLocaleDateString() : 'None';
          const newVal = metadata.dueDate.new ? new Date(metadata.dueDate.new).toLocaleDateString() : 'None';
          details.push(`Due Date: ${oldVal} → ${newVal}`);
        } else if (typeof metadata.dueDate === 'string') {
          const newVal = new Date(metadata.dueDate).toLocaleDateString();
          details.push(`Due Date: ${newVal}`);
        }
      }
      if (metadata.resourceId !== undefined) {
        if (typeof metadata.resourceId === 'object' && metadata.resourceId.old !== undefined) {
          const oldResource = metadata.resourceId.old ? (resources && Array.isArray(resources) ? resources.find((r: Resource) => r.id === metadata.resourceId.old)?.name : null) || 'Unknown' : 'None';
          const newResource = metadata.resourceId.new ? (resources && Array.isArray(resources) ? resources.find((r: Resource) => r.id === metadata.resourceId.new)?.name : null) || 'Unknown' : 'None';
          details.push(`Resource: ${oldResource} → ${newResource}`);
        } else if (metadata.resourceId) {
          const resource = resources && Array.isArray(resources) ? resources.find((r: Resource) => r.id === metadata.resourceId) : null;
          details.push(`Resource: ${resource?.name || 'Unknown'}`);
        }
      }
    } else if (activity.activityType === 'CREATED' && metadata) {
      if (metadata.type) {
        details.push(`Type: ${getDisplayLabel(metadata.type, problemTaskTypeLabels)}`);
      }
      if (metadata.assignedTo) {
        details.push(`Owner: ${getDisplayLabel(metadata.assignedTo, problemTaskAssignedToLabels)}`);
      }
    }

    return details;
  };

  return (
    <>
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed top-16 bottom-8 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-7xl">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="bg-gray-50 px-8 py-6 border-b border-gray-200 flex-shrink-0">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold text-gray-900 flex items-center justify-between"
                      >
                        <span>
                          {isEditMode ? `Edit Task: ${taskData?.number || existingTask?.number}` : 'Create New Problem Task'}
                        </span>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-500 hover:bg-gray-200 rounded-full p-1"
                          onClick={onClose}
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </Dialog.Title>
                    </div>

                    {/* Task Workflow Component */}
                    {isEditMode && (
                      <div className="bg-white px-8 py-6 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center justify-center">
                      {['new', 'in_progress', 'closed'].map((state, index) => (
                        <React.Fragment key={state}>
                          <button
                            onClick={() => canTransitionTo(taskState, state) && handleStateChange(state)}
                            disabled={!canTransitionTo(taskState, state) || loading}
                            className={`relative flex-1 px-6 py-4 text-sm font-medium transition-all ${
                              taskState === state
                                ? 'bg-blue-600 text-white z-10'
                                : canTransitionTo(taskState, state)
                                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            } ${index === 0 ? 'rounded-l-lg' : ''} ${index === 2 ? 'rounded-r-lg' : ''}`}
                            style={{
                              clipPath: index === 0
                                ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                : index === 1
                                ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)'
                                : 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)',
                              marginLeft: index > 0 ? '-20px' : '0'
                            }}
                          >
                            <span className={index > 0 ? 'ml-4' : ''}>
                              {getDisplayLabel(state, problemTaskStateLabels)}
                            </span>
                          </button>
                        </React.Fragment>
                      ))}
                        </div>
                      </div>
                    )}

                    {/* Tabs Navigation and Content */}
                    <div className="flex-1 flex flex-col min-h-0">
                      <Tab.Group as="div" className="flex flex-col flex-1 min-h-0">
                        <div className="border-b border-gray-200 px-8 flex-shrink-0">
                          <Tab.List className="flex space-x-8">
                            <Tab
                              className={({ selected }) =>
                                classNames(
                                  'py-4 px-1 border-b-2 font-medium text-sm focus:outline-none',
                                  selected
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                )
                              }
                            >
                              Task Detail
                            </Tab>
                            <Tab
                              className={({ selected }) =>
                                classNames(
                                  'py-4 px-1 border-b-2 font-medium text-sm focus:outline-none',
                                  selected
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                )
                              }
                            >
                              Notes
                            </Tab>
                            <Tab
                              className={({ selected }) =>
                                classNames(
                                  'py-4 px-1 border-b-2 font-medium text-sm focus:outline-none',
                                  selected
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                )
                              }
                            >
                              Activities
                            </Tab>
                            {taskState === 'closed' && (
                              <Tab
                                className={({ selected }) =>
                                  classNames(
                                    'py-4 px-1 border-b-2 font-medium text-sm focus:outline-none',
                                    selected
                                      ? 'border-blue-500 text-blue-600'
                                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                  )
                                }
                              >
                                Closure Information
                              </Tab>
                            )}
                          </Tab.List>
                        </div>

                        <Tab.Panels className="flex-1 overflow-y-auto">
                          {/* Task Detail Tab */}
                          <Tab.Panel className="px-8 py-6">
                            {error && (
                              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                                <div className="flex">
                                  <div className="flex-shrink-0">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm text-red-800">{error}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            <form id="problem-task-form" onSubmit={handleSubmit} className="space-y-5">
                    {/* Task Number and State (readonly, shown only for existing tasks) */}
                    {isEditMode && (
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                            Task Number
                          </label>
                          <input
                            type="text"
                            value={taskData?.number || ''}
                            disabled
                            className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                            State
                          </label>
                          <input
                            type="text"
                            value={getDisplayLabel(taskState, problemTaskStateLabels)}
                            disabled
                            className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                          />
                        </div>
                      </div>
                    )}

                    {/* Type and Problem */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                          Type
                        </label>
                        <input
                          type="text"
                          value={getDisplayLabel(taskType, problemTaskTypeLabels)}
                          disabled
                          className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                          Problem
                        </label>
                        <input
                          type="text"
                          value={problemNumber}
                          disabled
                          className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                        />
                      </div>
                    </div>

                    {/* Owner and Due Date */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                          Owner <span className="text-red-500">*</span>
                        </label>
                        <Combobox value={assignedTo} onChange={(value: string | null) => setAssignedTo(value || '')}>
                          <div className="relative flex-1">
                            <div className="relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                              <Combobox.Input
                                className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                                displayValue={() => selectedOwner?.name || ''}
                                onChange={(e) => setOwnerQuery(e.target.value)}
                                placeholder="Search owner..."
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                              </Combobox.Button>
                            </div>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setOwnerQuery('')}
                            >
                              <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {filteredOwnerOptions.length === 0 ? (
                                  <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                    No options found.
                                  </div>
                                ) : (
                                  filteredOwnerOptions.map((option) => (
                                    <Combobox.Option
                                      key={option.id}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`
                                      }
                                      value={option.id}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                            {option.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                      <div className="flex items-center gap-4">
                        <label htmlFor="dueDate" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                          Due Date
                        </label>
                        <div className="flex-1">
                          <DateTimePickerInput
                            id="dueDate"
                            value={dueDate}
                            onChange={(value) => setDueDate(value)}
                            showTimeSelect={false}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Resource */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">
                          Resource
                        </label>
                        <div className="flex-1">
                          <Combobox value={resourceId} onChange={(value: string | null) => setResourceId(value || '')} disabled={!engagementId || loadingResources}>
                            <div className="relative">
                              <div className={`relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${!engagementId || loadingResources ? 'bg-gray-100' : ''}`}>
                                <Combobox.Input
                                  className={`w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 ${!engagementId || loadingResources ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                  displayValue={() => selectedResource ? `${selectedResource.name} (${selectedResource.resourceType})` : ''}
                                  onChange={(e) => setResourceQuery(e.target.value)}
                                  placeholder={!engagementId ? 'No engagement selected' : loadingResources ? 'Loading...' : 'Search resource...'}
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  <Combobox.Option
                                    value=""
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-500'}`
                                    }
                                  >
                                    <span className="block truncate font-normal italic">None (Clear selection)</span>
                                  </Combobox.Option>
                                  {filteredResources.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                      No resources found.
                                    </div>
                                  ) : (
                                    filteredResources.map((resource) => (
                                      <Combobox.Option
                                        key={resource.id}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`
                                        }
                                        value={resource.id}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {resource.name} ({resource.resourceType})
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {!engagementId && (
                            <p className="mt-1.5 text-xs text-gray-500">
                              Problem must be associated with an engagement to select a resource
                            </p>
                          )}
                        </div>
                      </div>
                      <div></div>
                    </div>

                    {/* Assignment Details (editable for existing tasks) */}
                    {isEditMode && (
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex items-start gap-4">
                          <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2">
                            Assignment Group <span className="text-red-500">*</span>
                          </label>
                          <div className="flex-1">
                          <Combobox
                            value={assignmentGroupId}
                            onChange={(value: string) => {
                              setAssignmentGroupId(value);
                              setAssignedToId('');
                              setUserQuery('');
                              if (error === 'Assignment Group is required') setError(null);
                            }}
                          >
                            <div className="relative">
                              <div className={`relative w-full cursor-default overflow-hidden rounded-md border bg-white text-left shadow-sm ${
                                error === 'Assignment Group is required'
                                  ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500'
                                  : 'border-gray-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'
                              }`}>
                                <Combobox.Input
                                  className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                                  displayValue={() => selectedGroup?.name || ''}
                                  onChange={(e) => setGroupQuery(e.target.value)}
                                  placeholder="Search assignment groups..."
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setGroupQuery('')}
                              >
                                <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredGroups.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                      No groups found.
                                    </div>
                                  ) : (
                                    filteredGroups.map((group: any) => (
                                      <Combobox.Option
                                        key={group.id}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`
                                        }
                                        value={group.id}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                              {group.name}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {error === 'Assignment Group is required' && (
                            <p className="mt-1 text-sm text-red-600">{error}</p>
                          )}
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <label className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2">
                            Assigned To
                          </label>
                          <Combobox value={assignedToId} onChange={(value: string | null) => setAssignedToId(value || '')} disabled={!assignmentGroupId}>
                            <div className="relative flex-1">
                              <div className={`relative w-full cursor-default overflow-hidden rounded-md border border-gray-300 bg-white text-left shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 ${!assignmentGroupId ? 'bg-gray-100' : ''}`}>
                                <Combobox.Input
                                  className={`w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 ${!assignmentGroupId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                  displayValue={() => selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : ''}
                                  onChange={(e) => setUserQuery(e.target.value)}
                                  placeholder={!assignmentGroupId ? 'Select group first' : 'Search users...'}
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setUserQuery('')}
                              >
                                <Combobox.Options className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                  {filteredMembers.length === 0 ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                                      {!assignmentGroupId ? 'Select a group first' : 'No users found.'}
                                    </div>
                                  ) : (
                                    filteredMembers.map((member: any) => (
                                      <Combobox.Option
                                        key={member.id}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`
                                        }
                                        value={member.id}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <div className="flex flex-col">
                                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                {member.firstName} {member.lastName}
                                              </span>
                                              <span className={`block truncate text-xs ${active ? 'text-blue-200' : 'text-gray-500'}`}>
                                                {member.email}
                                              </span>
                                            </div>
                                            {selected && (
                                              <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    )}

                    {/* Task Description */}
                    <div className="flex items-start gap-4">
                      <label htmlFor="description" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2.5">
                        Task Description <span className="text-red-500">*</span>
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={description}
                          onChange={(value) => setDescription(value)}
                          placeholder="Enter detailed description of the task..."
                          minHeight="120px"
                        />
                      </div>
                    </div>

                    {/* Service Improvements */}
                    <div className="flex items-start gap-4">
                      <label htmlFor="serviceImprovements" className="text-sm font-medium text-gray-700 w-32 flex-shrink-0 pt-2.5">
                        Service Improvements
                      </label>
                      <div className="flex-1">
                        <RichTextEditor
                          value={serviceImprovements}
                          onChange={(value) => setServiceImprovements(value)}
                          placeholder="Enter service improvements..."
                          minHeight="120px"
                        />
                      </div>
                    </div>
                            </form>
                          </Tab.Panel>

                          {/* Notes Tab */}
                          <Tab.Panel className="px-8 py-6">
                            {!isEditMode ? (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                                <p className="text-yellow-800">Please save the task first to add notes</p>
                              </div>
                            ) : (
                              <div className="space-y-6">
                                {/* Add Note Section */}
                                {taskState !== 'closed' && (
                                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                                    <div className="flex items-center justify-between">
                                      <h4 className="text-sm font-medium text-gray-900">Add Note</h4>
                                      <div className="flex items-center space-x-4">
                                        <label className="inline-flex items-center">
                                          <input
                                            type="radio"
                                            value="internal"
                                            checked={noteType === 'internal'}
                                            onChange={(e) => setNoteType(e.target.value as 'internal')}
                                            className="form-radio h-4 w-4 text-blue-600"
                                          />
                                          <span className="ml-2 text-sm text-gray-700">Internal</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                          <input
                                            type="radio"
                                            value="customer"
                                            checked={noteType === 'customer'}
                                            onChange={(e) => setNoteType(e.target.value as 'customer')}
                                            className="form-radio h-4 w-4 text-blue-600"
                                          />
                                          <span className="ml-2 text-sm text-gray-700">Customer</span>
                                        </label>
                                      </div>
                                    </div>
                                    <RichTextEditor
                                      value={newNote}
                                      onChange={(value) => setNewNote(value)}
                                      placeholder={noteType === 'customer' ? 'Enter customer-facing note...' : 'Enter internal note...'}
                                      minHeight="120px"
                                    />
                                    <div className="flex justify-end">
                                      <button
                                        type="button"
                                        onClick={handleAddNote}
                                        disabled={addingNote || !newNote.trim()}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                      >
                                        {addingNote ? 'Adding...' : 'Add Note'}
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Notes List */}
                                <div className="space-y-4">
                                  <h4 className="text-sm font-medium text-gray-900">Notes History</h4>
                                  {loadingNotes ? (
                                    <div className="text-center py-8 text-gray-500">Loading notes...</div>
                                  ) : notes && notes.length > 0 ? (
                                    <div className="space-y-3">
                                      {notes.map((note) => (
                                        <div
                                          key={note.id}
                                          className={`border rounded-lg p-4 ${
                                            note.isPublic ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                                          }`}
                                        >
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <div className="flex items-center space-x-2 mb-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                  {note.User.firstName} {note.User.lastName}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                  {new Date(note.timestamp).toLocaleString()}
                                                </span>
                                                <span
                                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                    note.isPublic
                                                      ? 'bg-blue-100 text-blue-800'
                                                      : 'bg-gray-100 text-gray-800'
                                                  }`}
                                                >
                                                  {note.isPublic ? 'Customer' : 'Internal'}
                                                </span>
                                              </div>
                                              <RichTextDisplay content={note.note} className="text-sm text-gray-700" />
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => handleDeleteNote(note.id)}
                                              className="ml-4 text-red-600 hover:text-red-800 text-sm"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                                      <p>No notes yet</p>
                                      <p className="text-sm mt-1">Add your first note above</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </Tab.Panel>

                          {/* Activities Tab */}
                          <Tab.Panel className="px-8 py-6">
                            {!isEditMode ? (
                              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                                <p className="text-yellow-800">Please save the task first to view activities</p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-900">Activity History</h4>
                                {loadingActivities ? (
                                  <div className="text-center py-8 text-gray-500">Loading activities...</div>
                                ) : activities && activities.length > 0 ? (
                                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200 table-fixed">
                                      <colgroup>
                                        <col className="w-40" />
                                        <col className="w-36" />
                                        <col className="w-32" />
                                        <col />
                                      </colgroup>
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date/Time
                                          </th>
                                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Activity
                                          </th>
                                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                          </th>
                                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Details
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {activities.map((activity) => (
                                          <tr key={activity.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                              {new Date(activity.performedAt).toLocaleString('en-US', {
                                                month: '2-digit',
                                                day: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                              {getActivityDescription(activity)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                              {activity.User.firstName} {activity.User.lastName}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                              {(() => {
                                                const metadata = activity.metadata as any;
                                                // Special handling for note activities
                                                if ((activity.activityType === 'NOTE_ADDED_INTERNAL' || activity.activityType === 'NOTE_ADDED_CUSTOMER') && metadata?.note) {
                                                  return (
                                                    <div className={activity.activityType === 'NOTE_ADDED_CUSTOMER' ? 'text-blue-700' : 'text-gray-700'}>
                                                      <RichTextDisplay content={metadata.note} className="text-sm text-gray-700" />
                                                    </div>
                                                  );
                                                }
                                                // Default handling for other activity types
                                                const details = getActivityDetails(activity);
                                                return details.length > 0 ? (
                                                  details.map((detail, idx) => (
                                                    <div key={idx}>{detail}</div>
                                                  ))
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                );
                                              })()}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                                    <p>No activities yet</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </Tab.Panel>

                          {/* Closure Information Tab - Only visible when task is closed */}
                          {taskState === 'closed' && (
                            <Tab.Panel className="px-8 py-6">
                              <div className="space-y-6">
                                <h4 className="text-lg font-medium text-gray-900">Closure Information</h4>

                                <div className="space-y-5">
                                  {/* Closed Code */}
                                  <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                                      Closed Code
                                    </label>
                                    <input
                                      type="text"
                                      value={taskData?.closedCode || '-'}
                                      disabled
                                      className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                                    />
                                  </div>

                                  {/* Cause Code */}
                                  <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                                      Cause Code
                                    </label>
                                    <input
                                      type="text"
                                      value={taskData?.causeCode ? taskData.causeCode.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : '-'}
                                      disabled
                                      className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                                    />
                                  </div>

                                  {/* Cause Notes */}
                                  <div className="flex items-start gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0 pt-2.5">
                                      Cause Notes
                                    </label>
                                    <div className="flex-1 rounded-md border border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm min-h-[120px]">
                                      <RichTextDisplay content={taskData?.causeNotes || '-'} />
                                    </div>
                                  </div>

                                  {/* Proposed Fix Notes */}
                                  <div className="flex items-start gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0 pt-2.5">
                                      Proposed Fix Notes
                                    </label>
                                    <div className="flex-1 rounded-md border border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm min-h-[120px]">
                                      <RichTextDisplay content={taskData?.proposedFixNotes || '-'} />
                                    </div>
                                  </div>

                                  {/* Workaround */}
                                  <div className="flex items-start gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0 pt-2.5">
                                      Workaround
                                    </label>
                                    <div className="flex-1 rounded-md border border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm min-h-[120px]">
                                      <RichTextDisplay content={taskData?.workaround || '-'} />
                                    </div>
                                  </div>

                                  {/* Closed At */}
                                  <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                                      Closed At
                                    </label>
                                    <input
                                      type="text"
                                      value={taskData?.updatedAt ? new Date(taskData.updatedAt).toLocaleString() : '-'}
                                      disabled
                                      className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                                    />
                                  </div>

                                  {/* Closed By */}
                                  {taskData?.User_ProblemTask_updatedByIdToUser && (
                                    <div className="flex items-center gap-4">
                                      <label className="text-sm font-medium text-gray-700 w-40 flex-shrink-0">
                                        Closed By
                                      </label>
                                      <input
                                        type="text"
                                        value={`${taskData.User_ProblemTask_updatedByIdToUser.firstName} ${taskData.User_ProblemTask_updatedByIdToUser.lastName}`}
                                        disabled
                                        className="flex-1 rounded-md border-gray-300 bg-gray-50 shadow-sm py-2.5 px-3 sm:text-sm cursor-not-allowed"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Tab.Panel>
                          )}
                        </Tab.Panels>
                      </Tab.Group>
                    </div>

                    {/* Footer - Fixed at bottom */}
                    <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={onClose}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        form="problem-task-form"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                        disabled={loading}
                      >
                        {loading ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save Changes' : 'Create Task'}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* In Progress Modal */}
    {existingTask && (
      <InProgressModal
        isOpen={showInProgressModal}
        onClose={() => {
          setShowInProgressModal(false);
          setPendingStateChange(null);
        }}
        onConfirm={async (assignmentGroupId, assignedToId) => {
          if (pendingStateChange) {
            await performStateChange(pendingStateChange, assignmentGroupId, assignedToId, null);
            setPendingStateChange(null);
          }
        }}
        taskId={existingTask.id}
        customerId={customerId}
      />
    )}

    {/* Close Task Modal */}
    {existingTask && (
      <CloseTaskModal
        isOpen={showCloseTaskModal}
        onClose={() => {
          setShowCloseTaskModal(false);
          setPendingStateChange(null);
        }}
        onConfirm={async (closureData) => {
          if (pendingStateChange) {
            await performStateChange(pendingStateChange, null, null, closureData);
            setPendingStateChange(null);
          }
        }}
        taskId={existingTask.id}
      />
    )}
    </>
  );
};

export default ProblemTaskModal;
